import {
  useContextCalendars,
  useContextDatePickerOffsetPropGetters,
  useContextDays,
} from '@rehookify/datepicker';
import { useCallback, useId } from 'react';
import useTranslation from '~/hooks/useTranslation';
import DayHead from '~/components/Calendar/DayHead';
import Day from '~/components/Calendar/Day';
import useDayStateForDate, { DayState } from '~/hooks/calendar/useDayStateForDate';
import createOptions from '~/helpers/calendar/createOptions';
import Alert from '~/components/General/Alert';
import formStyles from '~/styles/form.module.css';
import utilStyles from '~/styles/utils.module.css';
import calendarStyles from '~/styles/calendar/calendar.module.css';
import useCalendarData from '~/hooks/calendar/useCalendarData';
import { useOptions } from '~/hooks/useOptions';
import { InferredCalendarOptions } from '~/types/options';
import { dateToString } from '~/helpers/dateToString';
import formatPrice from '~/helpers/formatPrice';
import IconChevronLeft from '~icons/tabler/chevron-left';
import IconChevronRight from '~icons/tabler/chevron-right';

type Props = {
  offsetDate: Date;
  setOffsetDate: (date: Date) => void;
  minDate: Date;
  maxDate: Date;
  pax: number;
  packageId: number;
  confirmButton: (selectedDayState: DayState | null) => React.ReactNode;
  contactButton: () => React.ReactNode;
};

export default function Calendar({
  offsetDate,
  setOffsetDate,
  minDate,
  maxDate,
  pax,
  packageId,
  confirmButton,
  contactButton,
}: Props) {
  const { calendars, weekDays } = useContextCalendars();
  const { selectedDates } = useContextDays();
  const { addOffset, subtractOffset } = useContextDatePickerOffsetPropGetters();
  const translate = useTranslation();
  const calendarMonthSelectId = useId();
  const options = useOptions() as InferredCalendarOptions;

  // Retrieve all days for the current view
  const { days } = calendars[0];

  // Create an array of available months for the select box
  const monthOptions = createOptions(minDate, maxDate, options.locale);

  const onChangeMonth = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.currentTarget;
    const monthOption = monthOptions.find((option) => option.formatted === value);

    if (monthOption) {
      setOffsetDate(new Date(monthOption.date));
    }
  }, [monthOptions, setOffsetDate]);

  // Get the active month for the select box
  const activeMonth = `${offsetDate.getMonth()}-${offsetDate.getFullYear()}`;

  // Find the active month in the array of available months
  const selectedMonth = monthOptions.find((monthOption) => {
    const monthOptionFormatted = `${monthOption.date.getMonth()}-${monthOption.date.getFullYear()}`;
    return monthOptionFormatted === activeMonth;
  });

  const firstDayFormatted = dateToString(days[0].$date);
  const lastDayFormatted = dateToString(days[days.length - 1].$date);

  // Retrieve the calendar data for the current view, those
  // are availabilities and prices
  const {
    data: calendarData,
    error: calendarDataError,
    isPending: calendarDataPending,
  } = useCalendarData({
    package_id: packageId,
    start_date: firstDayFormatted,
    end_date: lastDayFormatted,
    pax,
    locale: options.locale,
    office_id: options.officeId,
    department_id: options.departmentId,
  });

  const availabilities = calendarData?.availabilities ?? [];

  // This function will return the state of a day, based on the API data
  // some day might have a price, others don't, etc.
  const dayStateForDate = useDayStateForDate(availabilities);

  const selectedDayState = dayStateForDate(selectedDates[0] ?? null);

  if (calendarDataError) {
    return <Alert message={calendarDataError.message} />;
  }

  return (
    <>
      <div className={formStyles.formLabel}>
        {translate('calendar.arrival_date')}
      </div>

      <div className={calendarStyles.calendarWrapper}>
        {calendarDataPending && (
          <div className={calendarStyles.calendarLoader}>
            {translate('calendar.loading')}
          </div>
        )}

        <div className={calendarStyles.calendarHeader}>
          <button
            type="button"
            className={calendarStyles.calendarMonthSwitchBtn}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...subtractOffset({ months: 1 })}
          >
            <span className={utilStyles.srOnly}>{translate('calendar.previous_month')}</span>
            <span aria-hidden="true"><IconChevronLeft /></span>
          </button>

          <select
            aria-label={translate('calendar.select_year_month')}
            id={calendarMonthSelectId}
            className={calendarStyles.calendarHeaderMonth}
            value={selectedMonth?.formatted}
            onChange={onChangeMonth}
          >
            {monthOptions.map((monthOption) => (
              <option
                key={monthOption.formatted}
                value={monthOption.formatted}
              >
                {monthOption.formatted}
              </option>
            ))}
          </select>

          <button
            type="button"
            className={calendarStyles.calendarMonthSwitchBtn}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...addOffset({ months: 1 })}
          >
            <span className={utilStyles.srOnly}>{translate('calendar.next_month')}</span>
            <span aria-hidden="true"><IconChevronRight /></span>
          </button>
        </div>

        <div className={calendarStyles.calendarWeekDays}>
          {weekDays.map((day) => (
            <DayHead
              key={day}
              day={day}
            />
          ))}
        </div>

        <div className={calendarStyles.calendarDays}>
          {days.map((d) => (
            <Day
              key={d.$date.toString()}
              day={d}
              dayState={dayStateForDate(d.$date)}
            />
          ))}
        </div>
      </div>

      {selectedDayState && selectedDayState.priceRaw && selectedDayState.pricePerPersonRaw && (
        <div className={calendarStyles.totalPrice}>
          <div className={calendarStyles.totalPriceLabel}>
            {translate('calendar.total_price_from')}
          </div>
          <div className={calendarStyles.totalPriceValue}>
            <div className={calendarStyles.totalPriceValueLabel}>
              {formatPrice(selectedDayState.priceRaw)}
            </div>
            <div className={calendarStyles.totalPriceValueLabelSmall}>
              {`${formatPrice(selectedDayState.pricePerPersonRaw)} ${translate('calendar.p_p')}`}
            </div>
          </div>
        </div>
      )}

      {confirmButton(selectedDayState)}
      {availabilities.length <= 0 && contactButton()}
    </>
  );
}
